import React, { Suspense, useState } from 'react'

import { useLazyLoadQuery, graphql } from 'react-relay'
import { useLocation, useNavigate } from 'react-router-dom'

import { XStack, styled, View } from 'tamagui'

import { Text } from '../Text'
import { Link } from '../Link'
import { Spinner } from '../Spinner'
import { Group } from '../Group'
import { Icon } from '../Icon'

import { UserPrimaryPhoto } from '../User/UserPrimaryPhoto'

import { useAuthContext } from '@data/useAuthContext'

import {
  DynamicIslandProfileExpandQuery,
  DynamicIslandProfileExpandQuery$data,
} from './__generated__/DynamicIslandProfileExpandQuery.graphql'

export const DynamicIslandProfileExpand = () => {
  return (
    <Suspense fallback={<Spinner size='$5' color='$darkBlue9' />}>
      <DynamicIslandProfileExpandContent />
    </Suspense>
  )
}

const DynamicIslandProfileExpandContent = () => {
  const data = useLazyLoadQuery<DynamicIslandProfileExpandQuery>(
    graphql`
      query DynamicIslandProfileExpandQuery {
        viewer {
          slugId

          ...UserPrimaryPhotoNew_user
        }
      }
    `,
    {}
  )

  if (data.viewer) {
    return <DynamicIslandProfileExpandContentAuthenticated data={data} />
  }

  return <DynamicIslandProfileExpandContentUnauthenticated />
}

const DynamicIslandProfileExpandContentAuthenticated = ({
  data,
}: {
  data: DynamicIslandProfileExpandQuery$data
}) => {
  const [itemHovered, setItemHovered] = useState<
    'profile' | 'settings' | 'signOut' | undefined
  >()

  const navigate = useNavigate()

  const { setAuthTokens } = useAuthContext()

  return (
    <Group
      activeIndex={undefined}
      gap='$2'
      highlightedElementProps={{
        borderRadius: '$6',
        backgroundColor: '$lightBlue1',
      }}
      marginHorizontal={-4}
      onHoverOut={() => setItemHovered(undefined)}
    >
      <Group.Item index={0}>
        <Link
          href={`/${data.viewer!.slugId}`}
          hoverStyle={{ textDecorationLine: 'none' }}
          padding='$1'
          paddingRight='$5'
          onHoverIn={() => setItemHovered('profile')}
        >
          <XStack gap='$3' alignItems='center'>
            <UserPrimaryPhoto user={data.viewer!} size='$6' thinShadow />

            <Text>Profile</Text>
          </XStack>
        </Link>
      </Group.Item>

      <Group.Item index={1}>
        <Link
          href={`/${data.viewer!.slugId}/settings`}
          hoverStyle={{ textDecorationLine: 'none' }}
          padding='$1'
          paddingRight='$5'
          onHoverIn={() => setItemHovered('settings')}
        >
          <XStack gap='$3' alignItems='center'>
            <StyledIconWrapper
              backgroundColor={
                itemHovered == 'settings' ? '$lightBlue2' : '$grey3'
              }
            >
              <StyledIcon name='settings-filled' />
            </StyledIconWrapper>

            <Text>Settings</Text>
          </XStack>
        </Link>
      </Group.Item>

      <Group.Item index={2}>
        <Link
          href='#'
          hoverStyle={{ textDecorationLine: 'none' }}
          padding='$1'
          paddingRight='$5'
          onHoverIn={() => setItemHovered('signOut')}
          onPress={async (event) => {
            event.preventDefault()

            await setAuthTokens(undefined, undefined)

            navigate('/')
          }}
        >
          <XStack gap='$3' alignItems='center'>
            <StyledIconWrapper
              backgroundColor={
                itemHovered == 'signOut' ? '$lightBlue2' : '$grey3'
              }
            >
              <StyledIcon name='sign-out-filled' />
            </StyledIconWrapper>

            <Text>Sign Out</Text>
          </XStack>
        </Link>
      </Group.Item>
    </Group>
  )
}

const DynamicIslandProfileExpandContentUnauthenticated = () => {
  const [itemHovered, setItemHovered] = useState<
    'signIn' | 'signUp' | undefined
  >()

  const location = useLocation()

  return (
    <Group
      activeIndex={undefined}
      gap='$2'
      highlightedElementProps={{
        borderRadius: '$6',
        backgroundColor: '$lightBlue1',
      }}
      marginHorizontal={-4}
      onHoverOut={() => setItemHovered(undefined)}
    >
      <Group.Item index={0}>
        <Link
          href='/sign-in'
          options={{ state: { backgroundLocation: location } }}
          hoverStyle={{ textDecorationLine: 'none' }}
          padding='$1'
          paddingRight='$5'
          onHoverIn={() => setItemHovered('signIn')}
        >
          <XStack gap='$3' alignItems='center'>
            <StyledIconWrapper
              backgroundColor={
                itemHovered == 'signIn' ? '$lightBlue2' : '$grey3'
              }
            >
              <StyledIcon name='person' />
            </StyledIconWrapper>

            <Text>Sign In</Text>
          </XStack>
        </Link>
      </Group.Item>

      <Group.Item index={1}>
        <Link
          href='/sign-up'
          options={{ state: { backgroundLocation: location } }}
          hoverStyle={{ textDecorationLine: 'none' }}
          padding='$1'
          paddingRight='$5'
          onHoverIn={() => setItemHovered('signUp')}
        >
          <XStack gap='$3' alignItems='center'>
            <StyledIconWrapper
              backgroundColor={
                itemHovered == 'signUp' ? '$lightBlue2' : '$grey3'
              }
            >
              <StyledIcon name='add' />
            </StyledIconWrapper>

            <Text>Sign Up</Text>
          </XStack>
        </Link>
      </Group.Item>
    </Group>
  )
}

const StyledIconWrapper = styled(View, {
  animation: 'fastSpring',
  alignItems: 'center',
  justifyContent: 'center',
  // @ts-expect-error - borderRadius '50%' is a web-only value
  borderRadius: '50%',
  backgroundColor: '$grey3',
  width: '$6',
  height: '$6',
})

const StyledIcon = styled(Icon, {
  size: '$5',
  padding: 0,
})
